import { chunkArr, waitForTruthyVal } from "../helpers/utils";
const SHOPIIFY_MAX_RECOMMENDATIONS = 10;
async function getShopifyRecommendations(productId, opts) {
    let url = `${window.Shopify.routes.root}recommendations/products.json?product_id=${[productId]}`;
    if (opts?.limit) {
        url += `&limit=${opts.limit}`;
    }
    if (opts?.intent) {
        url += `&intent=${opts.intent}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to fetch Shopify recommendations: ${response.statusText}`);
    }
    const json = (await response.json());
    return json.products;
}
/**
 * returns product recommendations from Shopify and sorts products by size and fit availability
 *
 * recommendations are managed via Shopify's Search & Discovery app
 * unfortunately, Shopify doesn't allow us to pass in filters for size and fit, so in-stock is not guaranteed
 *
 * @param productId
 * @param size
 * @param fit
 * @returns
 */
export async function getShopifyInStockRecommendations(productId, size, fit) {
    const recs = await getShopifyRecommendations(productId, { limit: SHOPIIFY_MAX_RECOMMENDATIONS });
    const matchChecks = [];
    if (size) {
        matchChecks.push({
            field: 'size',
            value: size.toLowerCase(),
        });
    }
    if (fit) {
        matchChecks.push({
            field: 'fit',
            value: fit.toLowerCase(),
        });
    }
    return recs.map((rec) => {
        const matchFieldPositions = matchChecks.reduce((acc, check) => {
            return { ...acc, [check.field]: rec.options.find((o) => o.name.toLowerCase() === check.field)?.position || 0 };
        }, {});
        const hasAtLeastOneMatchField = matchChecks.some((check) => matchFieldPositions[check.field] > 0);
        if (!hasAtLeastOneMatchField)
            return rec;
        const matchingVariant = rec.variants.find((v) => {
            return v.available && matchChecks.every((check) => {
                const position = matchFieldPositions[check.field];
                if (position === 0)
                    return true; // product doesn't have this option, so the check is not applicable
                return v.options[position - 1]?.toLowerCase() === check.value;
            });
        });
        return matchingVariant ? { ...rec, matched_variant_sku: matchingVariant.sku } : rec;
    }).sort((a, b) => (a.matched_variant_sku ? -1 : 1) - (b.matched_variant_sku ? -1 : 1)); // sort by in-stock availability
}
export function getInStockRecommendations(options, metaOptions = {}) {
    return new Promise((resolve, reject) => {
        if (window.DYO) {
            const rules = getRulesByOptions(options);
            const timeoutID = setTimeout(() => {
                console.error("DYO in-stock recommmendation timeout");
                reject();
            }, window.UNTUCKitConfig.dyTimeout);
            const opts = {
                realtimeRules: rules, ...metaOptions
            };
            window.DYO.recommendationWidgetData(window.UNTUCKitConfig.inStockRecommendationStrategyId, opts, function (error, data) {
                clearTimeout(timeoutID);
                if (error) {
                    console.error(error);
                    reject();
                }
                else {
                    resolve(data);
                }
            });
        }
        else {
            console.warn("Dynamic Yield not available.");
            reject();
        }
    });
}
export function registerRecommendationElement(el) {
    if (window.DYO) {
        window.DYO.recommendations.registerElements(el);
    }
    else {
        console.warn("Dynamic Yield not available.");
    }
}
function getRulesByOptions(options) {
    const rules = [];
    if (options) {
        if (options.size) {
            rules.push(generateRule(1, "size", options.size));
        }
        if (options.fit) {
            rules.push(generateRule(2, "fit", options.fit));
        }
        if (options.vendor) {
            rules.push(generateRule(3, "vendor", options.vendor));
        }
        if (options.gender) {
            rules.push(generateRule(4, "gender", options.gender));
        }
        if (options.sku) {
            rules.push(generateRule(5, "sku", options.sku, "exclude"));
        }
    }
    return rules;
}
function generateRule(id, field, value, queryType = "include") {
    return {
        "id": id,
        "query": {
            "conditions": [{
                    "field": field,
                    "arguments": [{
                            "action": "IS",
                            "value": value
                        }]
                }]
        },
        "type": queryType,
        "slots": []
    };
}
export async function getSocialProof(skus, timespan, interaction) {
    const dyApiLoaded = await waitForTruthyVal(() => window.DY?.API !== undefined);
    if (dyApiLoaded) {
        const chunks = chunkArr(skus, 20);
        const promises = chunks.map((skuChunk) => {
            return new Promise((resolve, reject) => {
                window.DY.API('callback', () => {
                    window.DY.ServerUtil.getProductsData(skuChunk, [timespan], interaction, true, (err, res) => {
                        if (err)
                            reject(err);
                        resolve(res);
                    });
                });
            });
        });
        try {
            const dataArr = await Promise.all(promises);
            const data = dataArr.reduce((curr, acc) => {
                return { ...acc, ...curr };
            }, {});
            return data;
        }
        catch (err) {
            console.error("social proof error", err);
        }
    }
}
